import IFrame from '../ui/IFrame';
import * as Utils from '../ui/Utils';
import * as members from '../privates/privateMembers';
import * as postMessage from '../privates/postMessage';
import {validate} from '../privates/validations/validation';

/**
 * @callback $w.HtmlElementEventHandler
 * @description Handles events fired when a message is received from an HTML
 *  Component.
 * @param {$w.HtmlComponentMessageEvent} event The message event that occurred.
 * @param {$w.$w} $w Deprecated: A selector function. The $w function was used to
 *  enable event handlers to work with elements in [repeaters]($w.Repeater.html). Now, to get
 *  a scoped selector for working with repeater items, use the [$w.at()]($w.html#at) function
 *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`.
 */

/**
 * @class HtmlComponent
 * @summary A container for internal or external HTML code. 
 * @description
 *  Messages can be sent
 *  from your page code to the code in an HTML component and from the HTML
 *  component to your page code using the [`postMessage()`](#postMessage) and
 *  [`onMessage()`](#onMessage) functions. To learn more about what code an HTML
 *  component can hold, see <a href="https://support.wix.com/en/article/using-iframes-to-display-visible-content-on-your-site" target="_blank">here</a>.
 *
 *  For an overview of working with an HTML Component using code, see <a href="https://support.wix.com/en/article/working-with-the-html-element" target="_blank">Working with the HTML Element</a>.
 * @mixes $w.IFrame
 * @memberof $w
 * @queryable
 * @tagname IFrame
 * @viewername wysiwyg.viewer.components.HtmlComponent
 * @definitionId wysiwyg.viewer.components.HtmlComponent
 */

export default class HtmlComponent extends IFrame {

    // PROPERTIES:
  /**
   * @member src
   * @memberof $w.HtmlComponent
   * @param {String} url
   */
    set src(url) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'src',
            dataToValidate: {url},
            shouldCheckMandatoryFields: true,
            compName: this.id
        })) {
            return;
        }
        Utils.setData(this, {url});
    }

  /**
   * @member src
   * @label property
   * @syntax
   * get src(): string
   * set src(value: string): void
   * @summary Sets or gets the URL of the HTML Component's code.
   * @description
   *  Setting the `src` property sets the website that is
   *  displayed in the HTML Component. The `src` value must be set to an HTTPS
   *  URL.
   *
   *  Getting the `src` property returns the URL of the website
   *  that is displayed in the HTML Component.
   * @note
   *  The `src` property must be an HTTPS URL. To use an HTTP URL, [turn off
   *  SSL for your site](https://support.wix.com/en/article/turning-ssl-on-or-off-for-your-site).
   * @snippet [HtmlComponent-src_get.es6=Get the displayed website's URL]
   * @snippet [HtmlComponent-src_set.es6=Set the displayed website]
   * @type {String}
   * @memberof $w.HtmlComponent
   * @instance
   */
    get src() {
        return Utils.getData(this, 'url') || null;
    }

  /**
   * @member scrolling
   * @memberof $w.HtmlComponent
   * @param {String} value
   */
    set scrolling(value) {
        const compType = members._type.get(this).compType;
        const isValid = validate({
            compType,
            functionName: 'scrolling',
            dataToValidate: {
                scrolling: value
            },
            compName: this.id
        });
        if (!isValid) {
            return;
        }
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {
            scrolling: value
        });
    }

  /**
   * @member scrolling
   * @label property
   * @syntax
   * get scrolling(): ScrollType
   * set scrolling(value: ScrollType): void
   * type ScrollType = "auto" | "no" | "yes"
   * @summary Sets or gets whether the HTML Component displays scrollbars.
   * @description
   *  Setting the `scrolling` property sets what happens when the content in
   *  the HTML Component is larger than the size of the component.
   *
   *  The value can be set to:
   *
   *  + `"auto"`: Scrollbars are displayed only if needed. (This is the default.)
   *  + `"yes"`: Scrollbars are always shown, even if they are not needed.
   *  + `"no"`: Scrollbars are never shown, even if they are needed).
   *
   *  Getting the `scrolling` property returns which `ScrollType` the component
   *  is set to use.
   * @snippet [HtmlComponent-scrolling_set.es6=Set whether the Html Component displays scrollbars]
   * @snippet [HtmlComponent-scrolling_get.es6=Get whether the Html Component displays scrollbars]
   * @type {string}
   * @memberof $w.HtmlComponent
   */
    get scrolling() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        return RMI.getData(compId).scrolling || 'auto';
    }

    // METHODS:
    /**
     * @function postMessage
     * @syntax
     * function postMessage(message: string | number | boolean | object | array)
     * @summary Sends a message to the HTML Component.
     * @description
     *  The `postMessage()` function sends a message from your page code to the
     *  HTML Component.
     *
     *  To receive the message sent from the `postMessage()` function in your
     *  HTML Component, create an event handler for the `window.onmessage` event
     *  in the component's code. You create the event handler within an HTML
     *  `<script>` tag. You get the received data by getting the data property
     *  of the event handler's event parameter.
     *
     *  Often, you define the `window.onMessage` event handler in a function
     *  that gets called when the HTML component loads using the `body onload`
     *  or `window.onload`:
     *
     *      <script type="text/javascript">
     *        window.onmessage = (event) => {
     *          if (event.data) {
     *            console.log(`HTML Component received a message: ${event.data}`);
     *            // additional code here
     *          }
     *        }
     *      </script>
     *
     *
     *  For more information on sending and receiving messages between your page
     *  and your HTML Component, see <a href="https://support.wix.com/en/article/working-with-the-html-component-in-wix-code#messaging-1" target="_blank">Working with the HTML Component in Wix Code</a>.
     * @note
     *  An HTML Component needs to load before you can send it messages using the
     *  `postMessage()` function.
     *
     *  Usually an HTML Component finishes loading a
     *  short time after the page it is on finishes loading. So if you call
     *  `postMessage()` inside the page’s [`onReady()`]($w.html#onReady)
     *  event handler, the HTML Component might not be ready yet.  To call `postMessage()`
     *  as soon as possible after a page loads, send a
     *  message from the HTML Component to the page code as when the HTML
     *  Component is loaded and then call `postMessage()` upon receipt of that
     *  message.
     *
     *  Also, an HTML Component can send messages to the page it is on before the HTML Component
     *  itself has loaded completely. For example, this can happen if the HTML Component’s body,
     *  or one of its downloaded images, is large. To make sure the HTML component is loaded,
     *  send messages only after listening for an onLoad event in the HTML component.
     *
     *
     *
     * @snippet [HtmlComponent-postMessage.es6=Send a message to an HTML Component]
     * @snippet [HtmlComponent-full.es6=Send a message to an HTML Component and receive a confirmation]
     *  This example assumes you have created a page with an HTML Component
     *  whose ID is `myHtmlComponent` and a button whose onClick event handler
     *  is set to `messageSendButton_onClick`.
     *
     *  When the user clicks the button, a message is sent to the HTML Code
     *  element. Code inside the element receives and displays the message. Then
     *  it sends a message back to the page code. The page code receives and
     *  logs the message.
     * @param {String | Number | Boolean | Object | Array<*>} message The message to send to the HTML Component.
     * @see [onMessage( )](#onMessage)
     * @memberof $w.HtmlComponent
     */
    postMessage(msg) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'postMessage',
            dataToValidate: {message: msg},
            compName: this.id
        })) {
            return this;
        }
        const compId = members._compId.get(this);
        postMessage.sendMessageToHost(postMessage.Intent.WIX_CODE_SITE_API, {
            command: postMessage.MessageTypes.POST_HTML_MESSAGE,
            data: {
                id: compId,
                msg
            },
            controllerId: members._controllerId.get(this)
        });
        return this;
    }

    /**
     * @function onMessage
     * @syntax
     * function onMessage(handler: HtmlElementEventHandler): HtmlComponent
     * callback HtmlElementEventHandler(event: HtmlComponentMessageEvent, $w: Function): void
     * @summary Adds an event handler that runs when the HTML Component
     *  sends a message.
     * @description
     *  The `onMessage()` function allows your page code to receive messages
     *  from an HTML Component on your page. When a message is received,
     *  the specified event handler is executed and the message can be retrieved
     *  using `event.data`.
     *
     *  To send a message from your HTML Component, use the postMessage() function
     *  in the HTML component's code. Generally, you call postMessage()
     *  from within a function:
     *
     *      <script type="text/javascript">
     *        function sendReturnMessage(msg) {
     *          window.parent.postMessage(msg, "http://mysite.com");
     *        }
     *      </script>
     *
     *  When posting a message from within your HTML Component, you should specify your
     *  site's URL as the `targetOrigin`. If you use `"*"` instead, your message can
     *  be intercepted by a malicious site. To learn more, see <a href="https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage" target="_blank">Window.postMessage()</a>.
     *
     *  For more information on sending and receiving messages between your page
     *  and your HTML Component, see <a href="https://support.wix.com/en/article/working-with-the-html-component-in-wix-code#messaging-1" target="_blank">Working with the HTML Component in Wix Code</a>.
     * @note
     *  **Deprecation note:** The $w parameter of event handlers is being deprecated. To get
     *  a scoped selector for working with elements in repeater items, use the [$w.at()]($w.html#at) function
     *  and pass it the context property of the event parameter: `$item = $w.at(event.context)`. To learn more, see
     *  <a href="https://www.wix.com/code/home/forum/wix-tips-and-updates/removal-of-the-w-parameter-from-event-handlers" target="_blank">here</a>.
     * @snippet [HtmlComponent-onMessage.es6=Receive a message from an HTML Component]
     * @snippet [HtmlComponent-full.es6=Send a message to an HTML Component and receive a confirmation]
     *  This example assumes you have created a page with an HTML Component
     *  whose ID is `myHtmlComponent` and a button whose onClick event handler
     *  is set to `messageSendButton_onClick`.
     *
     *  When the user clicks the button, a message is sent to the HTML Code
     *  element. Code inside the element receives and displays the message. Then
     *  it sends a message back to the page code. The page code receives and
     *  logs the message.
     * @param {$w.HtmlComponentMessageEventHandler} handler The name of the function or
     *  the function expression to run when the HTML Component sends a message.
     * @returns {$w.HtmlComponent} The HTML Code element that triggered the event.
     * @see [postMessage( )](#postMessage)
     * @memberof $w.HtmlComponent
     * @eventtype message
     * @eventhandler
     */
    onMessage(cb) {
        const compType = members._type.get(this).compType;
        if (!validate({
            compType,
            functionName: 'onMessage',
            dataToValidate: {onMessage: cb},
            compName: this.id
        })) {
            return this;
        }
        const RMI = members._RMI.get(this);
        const contextId = members._contextId.get(this);
        const controllerId = members._controllerId.get(this);
        const compId = members._compId.get(this);
        RMI.registerEvent(contextId, compId, RMI.getEventTypes().MESSAGE, controllerId, cb);
        return this;
    }

    /**
     * @function allowFullScreen
     * @label property
     * @syntax
     * function allowFullScreen(): HtmlComponent
     * @summary Allows the HTML Component to be put into full screen mode.
     * @description
     *  By default, users cannot place the HTML Component in full screen mode. Calling
     *  this function allows a user to place the HTML Component in full screen mode.
     * @snippet [HtmlComponent-allowFullScreen.es6=Allow an HTML Component to be placed in full screen mode]
     * @returns {$w.HtmlComponent}
     * @memberof $w.HtmlComponent
     */
    allowFullScreen() {
        const RMI = members._RMI.get(this);
        const compId = members._compId.get(this);
        RMI.setData(compId, {
            allowFullScreen: true
        });
        return this;
    }
}
